import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate } from 'react-router-dom';

import { SnackbarProvider } from 'notistack';

import { makeStyles, createStyles } from '@mui/styles';
import { Theme } from '@mui/material';

import Error404 from 'pages/Error404';
import Page from 'components/Page';
import GroundStationOverviewPage from 'pages/GroundStationOverviewPage';
import AlertsOverviewPage from 'pages/AlertsOverviewPage';
import SatelliteOperationsPage from 'pages/SatelliteOperationsPage';
import OrganisationPage from 'pages/OrganisationPage';
import SelectSectionPage from 'pages/SelectSectionPage';
import OwnProfilePage from 'pages/OwnProfilePage';
import ScheduleDetailedPage from 'pages/ScheduleDetailedPage';
import SingleAlertPage from 'pages/SingleAlertPage';
import NotificationProvider from 'components/NotificationProvider';

import { useAppSelector, useAppDispatch } from 'hooks';
import { REFRESH_DELAY } from 'config';
import { handleLogin } from 'state';

import { alertsActions } from 'state/alerts';
import { antennasActions } from 'state/antennas';
import { AuthActions } from 'state/auth';
import { websocketActions } from 'state/websockets';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            height: '100vh',
            display: 'grid',
            gridTemplateRows: '1fr',
        },
        errorText: {
            padding: theme.spacing(1),
            color: theme.palette.text.primary,
            width: '100%',
        },
        error404: {
            margin: theme.spacing(2),
        },
        fixScroll: {
            overflow: 'hidden',
        },
    }),
);

const App = () => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { auth, websocket } = useAppSelector((state) => state);

    const { tokens, user } = auth;

    const [isRefreshing, setisRefreshing] = useState(false);

    //get Alerts
    const { alerts } = useAppSelector((state) => state);

    useEffect(() => {
        let handler: NodeJS.Timeout | null = null;

        if (
            auth.tokens.status === 'finished' &&
            alerts.list.status === 'idle'
        ) {
            dispatch(alertsActions.getAlertList());

            handler = setInterval(
                () => dispatch(alertsActions.getAlertList()),
                1000 * 60,
            );
        }

        return () => {
            if (handler) clearInterval(handler);
        };
    }, [auth.tokens.status]);

    //Get Antennas

    const { antennas } = useAppSelector((state) => state);

    useEffect(() => {
        let handler: NodeJS.Timeout | null = null;

        if (
            auth.tokens.status === 'finished' &&
            antennas.list.status === 'idle'
        ) {
            dispatch(antennasActions.getAntennaList()).then(() => {
                setisRefreshing(false);
            });
            setisRefreshing(true);

            handler = setInterval(() => {
                if (!isRefreshing) {
                    dispatch(antennasActions.getAntennaList()).then(() => {
                        setisRefreshing(false);
                    });
                    setisRefreshing(true);
                }
            }, 1000 * 60); // 1000 miliseconds
        }

        return () => {
            if (handler) clearInterval(handler);
        };
    }, [auth.tokens.status, antennas]);

    //start of App.tsx original code

    const [loginAttempted, setLoginAttempted] = useState(tokens.object != null);

    useEffect(() => {
        if (!loginAttempted)
            handleLogin(dispatch, auth, navigate).then(() => {
                setLoginAttempted(true);
            });
    }, [dispatch, auth, user, navigate]);

    useEffect(() => {
        const interval = setInterval(() => {
            if (auth.tokens.object)
                dispatch(AuthActions.getToken({ tokens: auth.tokens.object }));
        }, REFRESH_DELAY);
        return () => {
            clearInterval(interval);
        };
    }, [auth]);

    useEffect(() => {
        if (auth.tokens.status === 'finished' && websocket.status === 'idle') {
            dispatch(websocketActions.connect());
        }
    }, [auth, websocket]);

    return loginAttempted ? (
        <div className={classes.root}>
            <SnackbarProvider maxSnack={3}>
                <Routes>
                    <Route path="/" element={<SelectSectionPage />} />
                    <Route
                        path="/ground-stations"
                        element={
                            <GroundStationOverviewPage
                                isRefreshing={isRefreshing}
                                setisRefreshing={setisRefreshing}
                            />
                        }
                    />
                    <Route path="/alerts" element={<AlertsOverviewPage />} />
                    <Route
                        path="/satellite-operations"
                        element={<SatelliteOperationsPage />}
                    />
                    <Route
                        path="/schedule/:scheduleId"
                        element={<ScheduleDetailedPage />}
                    />
                    <Route
                        path="/alerts/:alertId"
                        element={<SingleAlertPage />}
                    />

                    <Route
                        path="/organisation"
                        element={<OrganisationPage />}
                    />

                    <Route path="/users/self" element={<OwnProfilePage />} />

                    <Route
                        element={
                            <Page>
                                <div className={classes.error404}>
                                    <Error404 link="/" />
                                </div>
                            </Page>
                        }
                    />
                </Routes>
                <NotificationProvider />
            </SnackbarProvider>
        </div>
    ) : (
        <></>
    );
};

export default App;
